/**
 * Groups live in the slide's free-form `additionalData`, written with the same
 * whole-slide `addSlide` dispatch ShapeAdjustHandles uses for adjust values. That
 * keeps the Redux slice, the API and the socket protocol completely unchanged —
 * groups simply ride inside the slide payload that already syncs.
 */

import { v4 as uuid } from 'uuid';
import { slideAction, saveMirrorToCloudinary } from '../../store/slideSlice';
import { slideAPI } from '../../store/api';
import { emitSlideAdd } from '../../Socket/Socket';
import { getUpdatedSlides } from '../../utils/helper';
import { getSnapshot } from '../../utils/utils';

/** Every group on a slide. Always an object, never undefined. */
export const readGroups = (slideData) => slideData?.additionalData?.groups || {};

/** Add a group over `memberIds`, returning the new map and the new group's id. */
export const createGroup = (groups, memberIds) => {
  const groupId = `group-${uuid()}`;
  return {
    groupId,
    groups: { ...groups, [groupId]: { id: groupId, memberIds: [...memberIds] } },
  };
};

/** Remove a group. Members keep their current geometry, which is already absolute. */
export const dissolveGroup = (groups, groupId) => {
  const next = { ...groups };
  delete next[groupId];
  return next;
};

/** Persist a groups map onto the slide, syncing collaborators and the database. */
export const writeGroups = async ({
  groups,
  currentSlideId,
  currentSlideData,
  existingSlides,
  mirrorSlides,
  presentationId,
  dispatch,
}) => {
  const slideData = {
    ...currentSlideData,
    additionalData: { ...currentSlideData.additionalData, groups },
  };

  dispatch(slideAction.addSlide({ slideId: currentSlideId, slideData }));

  const dataUrl = await getSnapshot();
  dispatch(saveMirrorToCloudinary({
    dataId: currentSlideId,
    mirrorSlideDataUrl: dataUrl,
    presentationId,
  }));
  emitSlideAdd({
    roomId: presentationId,
    mirrorData: { dataId: currentSlideId, mirrorSlideDataUrl: dataUrl },
    slideData: { slideId: currentSlideId, slideData },
  });

  const updatedSlides = getUpdatedSlides(slideData, 'slide', existingSlides);
  await slideAPI.addNewSlideInDatabase(
    presentationId, currentSlideId, slideData, dispatch,
    { slides: updatedSlides, mirrorSlides },
  );
};
